/* eslint-disable no-undef */

import { BaseEntity, isEntityOfType } from ".";
import { IGet_universe_types_type_id_ok } from "../service/esiclient";
import { StaticStorageClient } from "../service/staticstorageclient";
import { getCachedTypeEntity } from "./TypeEntityCache";

const azureStorageClient = new StaticStorageClient();

export class TypeEntity extends BaseEntity {
  constructor(id: number, props: any) {
    super(id, props);
    this._dataTypeName = "Type";
  }

  async toJson(dogma?: boolean): Promise<Excel.EntityCellValue> {
    const id = this._id;
    const rawProps = this.props;
    const props: Partial<IGet_universe_types_type_id_ok> = this.props;
    var entity = await super.toJson();
    // Properties
    if (entity.properties) {
      // - Data Type
      entity.properties["data_type"] = {
        type: Excel.CellValueType.string,
        basicValue: "Type",
      };
      // - Id
      delete entity.properties["type_id"];
      delete entity.properties["typeID"];
      // - Image
      const imageOptions = await azureStorageClient.get_image_type(id);
      if (typeof imageOptions === "string") {
        if (imageOptions.includes("render")) {
          entity.properties["image"] = {
            type: Excel.CellValueType.webImage,
            address: `https://images.evetech.net/types/${id}/render`,
            altText: props.name,
          };
        } else if (imageOptions.includes("icon")) {
          entity.properties["image"] = {
            type: Excel.CellValueType.webImage,
            address: `https://images.evetech.net/types/${id}/icon`,
            altText: props.name,
          };
        } else if (imageOptions.includes("bpc")) {
          entity.properties["image"] = {
            type: Excel.CellValueType.webImage,
            address: `https://images.evetech.net/types/${id}/bpc`,
            altText: props.name,
          };
        }
      }
      if (!entity.properties["market_group_id"]) {
        if (entity.properties["groupID"]) entity.properties["group_id"] = entity.properties["groupID"];
        if (entity.properties["marketGroupID"])
          entity.properties["marketgroup_id"] = entity.properties["marketGroupID"];
        if (entity.properties["raceID"]) entity.properties["race_id"] = entity.properties["raceID"];
        if (entity.properties["portionSize"]) entity.properties["portion_size"] = entity.properties["portionSize"];
        delete entity.properties["groupID"];
        delete entity.properties["marketGroupID"];
        delete entity.properties["raceID"];
        delete entity.properties["portionSize"];
        delete entity.properties["iconID"];
        delete entity.properties["graphicID"];
        delete entity.properties["soundID"];
      } else {
        entity.properties["marketgroup_id"] = entity.properties["market_group_id"];
        delete entity.properties["market_group_id"];
      }
      // Market prices
      const marketPrices: any = await azureStorageClient.get_prices();
      if (marketPrices !== null) {
        const marketPrice = marketPrices.find((p: any) => p.type_id == id);
        if (marketPrice) {
          // Add market prices to type entity
          if (marketPrice.adjusted_price) {
            entity.properties["Adjusted Price"] = {
              type: Excel.CellValueType.double,
              basicValue: marketPrice.adjusted_price,
              propertyMetadata: {
                sublabel: "ISK",
              },
            };
          }
          if (marketPrice.average_price) {
            entity.properties["Average Price"] = {
              type: Excel.CellValueType.double,
              basicValue: marketPrice.average_price,
              propertyMetadata: {
                sublabel: "ISK",
              },
            };
          }
        }
      }
      const materialList = await azureStorageClient.get_type_material_by_id(id);
      if (materialList && materialList.materials) {
        let material_list: Excel.CellValue[][] = [];
        for (const y of materialList.materials) {
          const MaterialTypeEntity = await getCachedTypeEntity(y.materialTypeID);
          const materialName = MaterialTypeEntity != null ? MaterialTypeEntity?.props.name : y.materialTypeID;
          material_list.push([
            {
              type: Excel.CellValueType.entity,
              text: materialName + " x " + y.quantity,
              properties: {
                material_type_id: {
                  type: Excel.CellValueType.double,
                  basicValue: y.materialTypeID,
                },
                quantity: {
                  type: Excel.CellValueType.double,
                  basicValue: y.quantity,
                },
              },
            },
          ]);
        }
        if (material_list.length) {
          entity.properties["refine_materials"] = {
            type: Excel.CellValueType.array,
            elements: material_list,
          };
        }
      }
      const repackagedVolume = await azureStorageClient.get_repackaged_volume_by_id(id);
      if (repackagedVolume) {
        entity.properties["volume_repackaged"] = {
          type: Excel.CellValueType.double,
          basicValue: repackagedVolume,
        };
      }
    }
    if (dogma) {
      const isSkills = await azureStorageClient.get_groups_in_category(16);
      const is_cat_6 = await azureStorageClient.get_groups_in_category(6);
      const is_cat_7 = await azureStorageClient.get_groups_in_category(7);
      const groupID = rawProps.groupID;
      let dogma_array = null;
      let dogma_data = null;
      //Ensure the group_id is a skills group_id before fetching data
      if (isSkills && isSkills?.filter((x) => x.groupID === groupID).length) {
        const raw_dogma: any = await azureStorageClient.get_skills_dogma();
        dogma_data = raw_dogma ? raw_dogma[id] ?? undefined : undefined;
      } else if (is_cat_6 && is_cat_6?.filter((x) => x.groupID === groupID).length) {
        const raw_dogma: any = await azureStorageClient.get_dogma_6();
        dogma_data = raw_dogma ? raw_dogma[id] ?? undefined : undefined;
      } else if (is_cat_7 && is_cat_7?.filter((x) => x.groupID === groupID).length) {
        const raw_dogma: any = await azureStorageClient.get_dogma_7();
        dogma_data = raw_dogma ? raw_dogma[id] ?? undefined : undefined;
      }

      if (dogma_data) {
        dogma_array = dogma_data.map((dgm: any) => {
          const properties: Record<string, any> = {
            value: {
              type: Excel.CellValueType.formattedNumber,
              basicValue: dgm.value,
              numberFormat: "##0",
            },
            attributeID: {
              type: Excel.CellValueType.formattedNumber,
              basicValue: dgm.attributeID,
              numberFormat: "##0",
            },
            attributeName: {
              type: Excel.CellValueType.string,
              basicValue: dgm.attributeName,
            },
          };

          if (dgm.unitID != null)
            properties["unitID"] = {
              type: Excel.CellValueType.formattedNumber,
              basicValue: dgm.unitID,
              numberFormat: "##0",
            };
          if (dgm.unitName != null)
            properties["unitName"] = { type: Excel.CellValueType.string, basicValue: dgm.unitName };
          if (dgm.categoryID != null)
            properties["categoryID"] = {
              type: Excel.CellValueType.formattedNumber,
              basicValue: dgm.categoryID,
              numberFormat: "##0",
            };
          if (dgm.categoryName != null)
            properties["categoryName"] = { type: Excel.CellValueType.string, basicValue: dgm.categoryName };

          return {
            type: Excel.CellValueType.entity,
            text: dgm.attributeName,
            properties,
          };
        });
        if (entity.properties && dogma_array.length) {
          entity.properties["dogma"] = {
            type: Excel.CellValueType.array,
            elements: dogma_array,
          };
        }
      }
      if (dogma_data && isSkills && entity.properties) {
        //add primary and secondary and difficulty as unique fields.
        //180, 181, 275
        const primary = dogma_data.find((dmg: { attributeID: number }) => dmg.attributeID === 180);
        if (primary && primary.value) {
          entity.properties["dogma_primary_attribute"] = {
            type: Excel.CellValueType.double,
            basicValue: primary.value,
          };
        }

        const secondary = dogma_data.find((dmg: { attributeID: number }) => dmg.attributeID === 181);
        if (secondary && secondary.value) {
          entity.properties["dogma_secondary_attribute"] = {
            type: Excel.CellValueType.double,
            basicValue: secondary.value,
          };
        }

        const multiplier = dogma_data.find((dmg: { attributeID: number }) => dmg.attributeID === 275);
        if (multiplier && multiplier.value) {
          entity.properties["dogma_training_multiplier"] = {
            type: Excel.CellValueType.double,
            basicValue: multiplier.value,
          };
        }
      }
    }
    // Layouts
    if (entity.properties?.image) {
      entity.layouts = {
        card: {
          title: { property: "name" },
          mainImage: { property: "image" },
        },
        compact: {
          icon: "Plane",
        },
      };
    } else {
      entity.layouts = {
        card: {
          title: { property: "name" },
        },
        compact: {
          icon: "Plane",
        },
      };
    }
    return entity;
  }
}

export function isTypeEntity(entity: any): boolean {
  return isEntityOfType(entity, "Type");
}
